import { SERVICE_ORDER_KINDS, SERVICE_ORDER_STATUSES } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, reasonSchema } from './clinical-common.ts';
import { imagingReportSchema } from './imaging.ts';
import { LAB_GATE_STATES, labOrderSchema, releasedLabReportSchema } from './lab.ts';

// Orders from the consultation (docs/plans/phase1-consultation.md §3.3, C2): procedures
// and minimal imaging requests. Lab orders use the lab slice's contract unchanged. The
// reason and outcome are PHI: bodies only.

export { SERVICE_ORDER_KINDS, SERVICE_ORDER_STATUSES };

/**
 * Routes are per kind (`/procedure-orders`, `/imaging-orders`), so each declares exactly
 * the permission it needs.
 */
export const SERVICE_ORDER_PATHS = {
  procedure: 'procedure-orders',
  imaging: 'imaging-orders',
} as const;

export const serviceItemSearchRequestSchema = z.object({
  query: z.string().trim().min(2).max(100),
  visitId: z.uuid(),
});

export const serviceItemSearchResultSchema = z.object({
  items: z.array(
    z.object({
      serviceItemId: z.uuid(),
      code: z.string(),
      name: z.string(),
      /** Price for this visit's payer, or why there isn't one. */
      price: z.union([
        z.object({ state: z.literal('priced'), unitPriceCents: z.string() }),
        z.object({ state: z.literal('not_priced'), message: z.string() }),
      ]),
    }),
  ),
});

const outcomeTextSchema = z.string().trim().min(1).max(20_000);

export const placeServiceOrdersRequestSchema = z.object({
  clientRequestId: z.uuid(),
  consultationId: z.uuid().nullable().default(null),
  orders: z
    .array(
      z.object({
        serviceItemId: z.uuid(),
        priority: z.enum(['routine', 'urgent']).default('routine'),
        reason: z.string().trim().max(500).nullable().default(null),
        /** "Do it now": recorded as performed in the same request. */
        performNow: z
          .object({ performedAt: z.iso.datetime().optional(), outcomeText: outcomeTextSchema })
          .nullable()
          .default(null),
      }),
    )
    .min(1)
    .max(20),
});
export type PlaceServiceOrdersRequest = z.input<typeof placeServiceOrdersRequestSchema>;

export const serviceOrderSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  patientId: z.uuid(),
  kind: z.enum(SERVICE_ORDER_KINDS),
  serviceItemId: z.uuid(),
  itemName: z.string(),
  itemCode: z.string(),
  priority: z.enum(['routine', 'urgent']),
  reason: z.string().nullable(),
  orderedAt: z.iso.datetime(),
  orderingUser: actorSchema,
  status: z.enum(SERVICE_ORDER_STATUSES),
  /** Whether it can be done yet, in the lab's words (pay-before-service, approval). */
  gate: z.enum(LAB_GATE_STATES),
  performedAt: z.iso.datetime().nullable(),
  performedBy: actorSchema.nullable(),
  outcomeText: z.string().nullable(),
  statusReason: z.string().nullable(),
  amendments: z.array(z.object({ at: z.iso.datetime(), by: actorSchema, reason: z.string() })),
});
export type ServiceOrderView = z.infer<typeof serviceOrderSchema>;

export const placeServiceOrdersResultSchema = z.object({
  orders: z.array(serviceOrderSchema),
  /** Passive: the same service is already requested on this visit (C20). */
  warnings: z.array(z.object({ serviceItemId: z.uuid(), message: z.string() })),
});

export const performServiceOrderRequestSchema = z.object({
  performedAt: z.iso.datetime().optional(),
  outcomeText: outcomeTextSchema.nullable().default(null),
});

export const amendServiceOrderRequestSchema = z.object({
  outcomeText: outcomeTextSchema,
  reason: reasonSchema,
});

export const cancelServiceOrderRequestSchema = z.object({ reason: reasonSchema });

export const cancelServiceOrderResultSchema = z.object({
  order: serviceOrderSchema,
  /** What happened to the charge: a paid one needs a credit note from billing. */
  charge: z.enum(['cancelled', 'needs_credit_note', 'nothing_to_cancel']),
});

/** Every order on a visit, and released lab results (plan §7.2). */
export const visitOrdersSchema = z.object({
  lab: z.array(labOrderSchema),
  services: z.array(serviceOrderSchema),
  /** Released lab reports for this visit's orders; empty without `lab.results.read`. */
  results: z.array(releasedLabReportSchema),
  resultsHidden: z.boolean(),
  /** Signed imaging reports for this visit's orders; empty without `imaging.results.read`. */
  imagingReports: z.array(imagingReportSchema),
  imagingReportsHidden: z.boolean(),
});
export type VisitOrdersView = z.infer<typeof visitOrdersSchema>;

export const serviceOrderTodoQuerySchema = z.object({
  status: z.enum(['requested', 'performed']).default('requested'),
});

export const serviceOrderTodoSchema = z.object({
  items: z.array(
    serviceOrderSchema.extend({
      patient: z.object({ displayName: z.string(), patientNumber: z.string() }),
    }),
  ),
});
